import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { testProviderWithCooldown, getCooldownUntil } from "../lib/testCooldown";
import { Users, Mail, Key, Trash2, Copy, Shield, UserPlus, Loader2, Lock } from "lucide-react";
import HelpBubble from "../components/HelpBubble";

const ROLE_COLORS = {
  admin: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  buyer: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  viewer: "bg-zinc-500/15 text-slate-600 border-zinc-500/30",
};

// Module-scope pure component — hoisting out of Team() prevents React from
// unmounting/remounting on every parent render, which used to blow away the
// input focus + the `editing`/`rowSaving` maps on each keystroke.
function KeyRow({
  label, field, current, type = "password", placeholder, testProvider,
  keys, setKeys, editing, setEditing, rowSaving, onSave, onClear,
  onTest, testBusy, testResults, now,
}) {
  const isEditing = !!editing[field];
  const isSet = !!current;
  const showMaskedBadge = isSet && !isEditing;
  const saving = !!rowSaving[field];
  // Cooldown countdown — re-reads sessionStorage on every tick so multi-tab
  // cooldowns stay in sync.
  const cooldownUntil = testProvider ? getCooldownUntil(testProvider) : null;
  const secsLeft = cooldownUntil ? Math.max(0, Math.ceil((cooldownUntil - (now || Date.now())) / 1000)) : 0;
  const onCooldown = secsLeft > 0;
  return (
    <div className="grid grid-cols-[180px,1fr,auto] items-start gap-3">
      <label className="text-xs text-slate-500 font-mono pt-1.5">{label}</label>
      <div className="space-y-1">
        {showMaskedBadge ? (
          <div
            data-testid={`key-current-${field}`}
            className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5 text-xs font-mono text-slate-600"
          >
            <Lock className="w-3 h-3 text-emerald-600" />
            <span className="flex-1 truncate" title={`Stored: ${current}`}>Stored: <span className="text-slate-900">{current}</span></span>
          </div>
        ) : (
          <input
            type={type}
            value={keys[field] || ""}
            onChange={(e) => setKeys({ ...keys, [field]: e.target.value })}
            placeholder={placeholder}
            autoFocus={isEditing}
            data-testid={`key-${field}`}
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
        )}
      </div>
      <div className="flex items-center gap-1.5 pt-1">
        <span
          className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
            isSet ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-600"
                  : "bg-zinc-800/50 border-zinc-700 text-slate-500"
          }`}
          data-testid={`key-badge-${field}`}
        >
          {isSet ? "set" : "missing"}
        </span>
        {showMaskedBadge ? (
          <>
            <button
              onClick={() => setEditing({ ...editing, [field]: true })}
              data-testid={`edit-${field}`}
              className="text-[10px] font-medium px-2 py-0.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 rounded flex items-center gap-1"
            >
              Edit
            </button>
            <button
              onClick={() => onClear(field)}
              disabled={saving}
              data-testid={`clear-${field}`}
              className="text-[10px] font-medium px-2 py-0.5 bg-white hover:bg-red-50 border border-slate-300 text-red-600 rounded flex items-center gap-1"
              title="Clear this key (unsets for the whole workspace)"
            >
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          </>
        ) : (
          <>
            {isSet && (
              <button
                onClick={() => { setEditing({ ...editing, [field]: false }); setKeys({ ...keys, [field]: "" }); }}
                data-testid={`cancel-${field}`}
                className="text-[10px] font-medium px-2 py-0.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-600 rounded"
              >Cancel</button>
            )}
            <button
              onClick={() => onSave(field)}
              disabled={!keys[field] || saving}
              data-testid={`save-${field}`}
              className="text-[10px] font-medium px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {saving && <Loader2 className="w-2.5 h-2.5 animate-spin" />} Save
            </button>
          </>
        )}
        {testProvider && (
          <>
            <button
              onClick={() => onTest(testProvider)}
              disabled={!isSet || testBusy === testProvider || onCooldown}
              data-testid={`test-ws-${testProvider}-btn`}
              className="text-[10px] font-medium px-2 py-0.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              title={
                !isSet ? "Save a key first"
                : onCooldown ? `Cooldown — wait ${secsLeft}s (prevents self-inflicted rate-limits)`
                : "Live-ping this provider"
              }
            >
              {testBusy === testProvider ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : null}
              {onCooldown ? `Test (${secsLeft}s)` : "Test"}
            </button>
            {testResults[testProvider] && (
              <span
                data-testid={`test-ws-${testProvider}-result`}
                className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                  testResults[testProvider].ok
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-red-50 text-red-700 border-red-200"
                }`}
                title={testResults[testProvider].ok ? testResults[testProvider].detail : (testResults[testProvider].error || testResults[testProvider].hint || "")}
              >
                {testResults[testProvider].ok ? `✓ ${testResults[testProvider].latency_ms}ms` : "✗ failed"}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Team() {
  const [workspace, setWorkspace] = useState(null);
  const [team, setTeam] = useState({ members: [], invitations: [] });
  const [keys, setKeys] = useState({
    workspace_name: "",
    resend_api_key: "",
    sender_email: "",
    fal_api_key: "",
    elevenlabs_api_key: "",
    s1_auth_key: "",
    s1paid: "",
  });
  const [invite, setInvite] = useState({ email: "", name: "", role: "buyer" });
  const [busy, setBusy] = useState({ invite: false, keys: false, test: null });
  const [testResults, setTestResults] = useState({});
  const [lastInvite, setLastInvite] = useState(null);
  const [editing, setEditing] = useState({});       // per-field row-unlock state
  const [rowSaving, setRowSaving] = useState({});

  const load = async () => {
    try {
      const [{ data: ws }, { data: t }] = await Promise.all([
        api.get("/workspace"),
        api.get("/workspace/team"),
      ]);
      setWorkspace(ws);
      setTeam(t);
      setKeys((k) => ({ ...k, workspace_name: ws.name || "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load workspace");
    }
  };
  useEffect(() => { load(); }, []);

  // Ticking timer for Test-button cooldowns. MUST be before any early-return
  // so the hook order stays stable for the non-admin code path too.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (workspace && workspace.my_role !== "admin") {
    return (
      <div className="p-6" data-testid="team-page">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>Team</h1>
        <div className="mt-4 bg-white border border-slate-200 rounded-md p-6 text-sm text-slate-500">
          Only admins can manage team and workspace keys.
        </div>
      </div>
    );
  }

  const saveSingleKey = async (field) => {
    const val = keys[field];
    if (!val || !String(val).trim()) return toast.error(`${field} required`);
    setRowSaving((s) => ({ ...s, [field]: true }));
    try {
      await api.post("/workspace/keys", { [field]: val.trim() });
      toast.success(`${field} saved`);
      setKeys((k) => ({ ...k, [field]: "" }));
      setEditing((e) => ({ ...e, [field]: false }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setRowSaving((s) => ({ ...s, [field]: false }));
    }
  };

  const clearKey = async (field) => {
    if (!confirm(`Clear the stored ${field}? This will unset it for the whole workspace.`)) return;
    setRowSaving((s) => ({ ...s, [field]: true }));
    try {
      await api.post("/workspace/keys", { [field]: "" });
      toast.success(`${field} cleared`);
      setKeys((k) => ({ ...k, [field]: "" }));
      setEditing((e) => ({ ...e, [field]: false }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setRowSaving((s) => ({ ...s, [field]: false }));
    }
  };

  const sendInvite = async () => {
    if (!invite.email) return toast.error("Email required");
    setBusy((b) => ({ ...b, invite: true }));
    try {
      const { data } = await api.post("/workspace/invite", invite);
      setLastInvite(data);
      if (data.email?.sent) toast.success(`Email sent to ${invite.email}`);
      else toast.warning("Email couldn't send — use the link below");
      setInvite({ email: "", name: "", role: "buyer" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy((b) => ({ ...b, invite: false }));
    }
  };

  const removeMember = async (user_id) => {
    if (!confirm("Remove this member from the workspace?")) return;
    try {
      await api.delete(`/workspace/team/${user_id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const changeRole = async (user_id, role) => {
    try {
      await api.patch(`/workspace/team/${user_id}/role`, { role });
      toast.success(`Role → ${role}`);
      load();
    } catch {
      toast.error("Role change failed");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const K = workspace?.keys_status || {};

  // Ticking timer so Test-button countdowns update every second without
  // each KeyRow having its own setInterval.

  const testKey = async (provider) => {
    setBusy((b) => ({ ...b, test: provider }));
    const data = await testProviderWithCooldown(provider);
    if (data) setTestResults((r) => ({ ...r, [provider]: data }));
    setBusy((b) => ({ ...b, test: null }));
  };




  return (
    <div className="p-6 max-w-[1100px]" data-testid="team-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">Team & Workspace <HelpBubble feature="invite" /></h1>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          Admin-only · {workspace?.name} · {team.members.length} member(s)
        </p>
      </div>

      {/* Workspace keys */}
      <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Key className="w-4 h-4 text-amber-600" />
          <div className="text-sm font-medium">Workspace API Keys</div>
          <span className="text-[10px] font-mono bg-amber-500/10 text-amber-700 border border-amber-500/20 rounded px-1.5 py-0.5">admin only</span>
        </div>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
          Set these once — all Buyers in this workspace automatically inherit them. Leave a field blank to keep the current value.
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-[180px,1fr] items-center gap-3">
            <label className="text-xs text-slate-500 font-mono">Workspace name</label>
            <input
              value={keys.workspace_name}
              onChange={(e) => setKeys({ ...keys, workspace_name: e.target.value })}
              data-testid="key-workspace_name"
              className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500/40"
            />
          </div>
          {(() => {
            const rowProps = {
              keys, setKeys, editing, setEditing, rowSaving,
              onSave: saveSingleKey, onClear: clearKey,
              onTest: testKey, testBusy: busy.test, testResults, now,
            };
            return (
              <>
                <KeyRow {...rowProps} label="System1 auth_key" field="s1_auth_key" current={K.s1_auth_key} placeholder="from your S1 account manager" testProvider="s1" />
                <KeyRow {...rowProps} label="System1 s1paid"   field="s1paid"      type="text" current={K.s1paid}      placeholder="rsoc.rct.infobuzzes.001" />
                <KeyRow {...rowProps} label="Resend API key"   field="resend_api_key" current={K.resend_api_key} placeholder="re_..." testProvider="resend" />
                <KeyRow {...rowProps} label="Sender email"     field="sender_email"   type="email" current={K.sender_email} placeholder="digest@yourdomain.com" />
                <KeyRow {...rowProps} label="fal.ai API key"   field="fal_api_key"    current={K.fal_api_key} placeholder="fal_..." testProvider="fal" />
                <KeyRow {...rowProps} label="ElevenLabs API key" field="elevenlabs_api_key" current={K.elevenlabs_api_key} placeholder="sk_..." testProvider="elevenlabs" />
              </>
            );
          })()}
        </div>
      </div>

      {/* Invite member */}
      <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-4 h-4 text-blue-600" />
          <div className="text-sm font-medium">Invite team member</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,140px,auto] gap-2">
          <input
            type="email"
            value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            placeholder="buyer@company.com"
            data-testid="invite-email"
            className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
          <input
            value={invite.name}
            onChange={(e) => setInvite({ ...invite, name: e.target.value })}
            placeholder="Name (optional)"
            data-testid="invite-name"
            className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
          <select
            value={invite.role}
            onChange={(e) => setInvite({ ...invite, role: e.target.value })}
            data-testid="invite-role"
            className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono"
          >
            <option value="buyer">Media Buyer</option>
            <option value="viewer">Viewer</option>
          </select>
          <button
            onClick={sendInvite}
            disabled={busy.invite}
            data-testid="send-invite-btn"
            className="bg-blue-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-1.5"
          >
            <Mail className="w-3.5 h-3.5" /> {busy.invite ? "…" : "Send invite"}
          </button>
        </div>
        {lastInvite && (
          <div className="mt-3 p-3 bg-white border border-slate-200 rounded-md text-xs">
            <div className="text-slate-500 mb-1.5">
              {lastInvite.email?.sent
                ? `✓ Email delivered · you can also share this link directly:`
                : `⚠ Email couldn't send (${lastInvite.email?.error?.slice(0, 80) || "unknown"}). Share this link manually:`}
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-black text-slate-600 rounded px-2 py-1 overflow-x-auto whitespace-nowrap text-[11px]" data-testid="invite-link">{lastInvite.invite_link}</code>
              <button onClick={() => copy(lastInvite.invite_link)} className="text-slate-500 hover:text-slate-900"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Members */}
      <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-slate-500" />
          <div className="text-sm font-medium">Members ({team.members.length})</div>
        </div>
        <div className="space-y-2" data-testid="members-list">
          {team.members.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between bg-white border border-slate-200 rounded p-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-mono">
                  {(m.name || m.email).slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm text-slate-900">{m.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{m.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={m.role || "buyer"}
                  onChange={(e) => changeRole(m.user_id, e.target.value)}
                  disabled={m.user_id === workspace?.owner_user_id}
                  data-testid={`role-${m.user_id}`}
                  className={`text-[11px] font-mono rounded border px-1.5 py-0.5 ${ROLE_COLORS[m.role || "buyer"]} bg-transparent cursor-pointer disabled:cursor-not-allowed`}
                >
                  <option value="admin">admin</option>
                  <option value="buyer">buyer</option>
                  <option value="viewer">viewer</option>
                </select>
                {m.user_id !== workspace?.owner_user_id && (
                  <button onClick={() => removeMember(m.user_id)} data-testid={`remove-${m.user_id}`} className="text-slate-500 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending invitations */}
      {team.invitations.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-medium">Pending invitations ({team.invitations.length})</div>
          </div>
          <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
            If the emailed link didn't work (inbox mangled it), copy the link below and share directly via Slack/WhatsApp.
          </p>
          <div className="space-y-2">
            {team.invitations.map((inv, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded p-2.5" data-testid={`pending-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Shield className="w-3 h-3 text-slate-500" />
                    <span className="font-mono text-xs text-slate-700">{inv.email}</span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${ROLE_COLORS[inv.role]}`}>{inv.role}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    expires {new Date(inv.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-black text-amber-200 rounded px-2 py-1 overflow-x-auto whitespace-nowrap text-[10px]" data-testid={`pending-link-${i}`}>
                    {inv.invite_link}
                  </code>
                  <button
                    onClick={() => copy(inv.invite_link)}
                    data-testid={`copy-pending-${i}`}
                    className="flex items-center gap-1 bg-white border border-slate-200 hover:border-blue-400 rounded px-2 py-1 text-[11px] text-slate-900"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
